import * as React from 'react';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {safelyGetFilterByKey} from 'nutshell-core/filter';
import type {FilterObject} from 'nutshell-core/types';

import {Button} from 'shells/button/button';
import {LAST_UPDATED_VALUE_OPTIONS} from 'shells/control-bar/constants';
import {ExpandedFilterBar} from 'shells/control-bar/expanded-filter-bar';
import {
    getArchivedValue,
    getCampaignStatusesForQuery,
    getLastUpdatedValue,
} from 'shells/control-bar/helpers';
import {ListPageControlBar} from 'shells/control-bar/list-page-control-bar';
import {ViewBoardIcon as BoardIcon} from 'shells/icon/svg/view-board-icon';
import {ViewListIcon as ListIcon} from 'shells/icon/svg/view-list-icon';
import {TabbedContent} from 'shells/layout/tabbed-content';
import {SingleInputModal} from 'shells/single-input-modal/single-input-modal';

import type {DripSequenceStubFragment as DripSequence} from 'nutshell-graphql-types';

import {appHistory} from '../../../history';
import {writeLocalStorage} from '../../../util/localStorage';

import {GetSessionUser} from '../../core/get-session-user';
import {PageContainer, PageTitleBar} from '../../layout';

import {checkIsFiltering} from '../email-marketing-helpers';
import {DripSequenceListQuery} from '../graphql-wrappers/drip-sequence/drip-sequence-list-query';
import {DripSequenceMutations} from '../graphql-wrappers/drip-sequence/drip-sequence-mutations';
import {getDripSequenceListQueryVariables} from '../graphql-wrappers/helpers';
import {MarketingStatsBanner} from '../marketing-stats-banner/marketing-stats-banner';

import {DripSequencesBoardView} from './drip-sequences-board-view';
import {DripSequencesListView} from './drip-sequences-list-view';

type OwnProps = ContextRouter & {
    activeFilters: FilterObject[];
    onParamChange: (arg1: any, arg2: boolean) => any;
    urlParams: any;
    getUpdatedUrl: (arg1: any) => string;
    onUpdateFilters: (arg1: FilterObject) => void;
    onRemoveAllFilters: () => void;
};

type Props = OwnProps & {
    isLoading: boolean;
    error: string | null | undefined;
    dripSequences: DripSequence[];
    onCreateDripSequence: (name: string) => Promise<any>;
    onDuplicateDripSequence: (name: string, id: string) => Promise<any>;
    onDeleteDripSequence: (dripSequenceId: string) => Promise<any>;
    onRestoreDripSequence: (dripSequenceId: string) => Promise<any>;
    searchValue: string;
    urlParams: any;
    userId: string;
};

const DRIP_SEQUENCES_VIEW_SEARCH = 'DRIP_SEQUENCES_VIEW_SEARCH';

function DripSequencesListPageComponent(props: Props) {
    const [isSavingNewDripSequence, setIsSavingNewDripSequence] = React.useState<boolean>(false);
    const [duplicatingDripSequenceId, setDuplicatingDripSequenceId] = React.useState<
        string | null | undefined
    >(null);
    const autoCreateRef = React.useRef(false);

    const selectedTab = props.urlParams.view || 'board';

    // Support ?create=true from command palette AI action
    React.useEffect(() => {
        if (props.urlParams.create === 'true' && !autoCreateRef.current) {
            autoCreateRef.current = true;
            // Clear the param so refresh doesn't re-trigger
            props.onParamChange({create: undefined}, true);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        writeLocalStorage(DRIP_SEQUENCES_VIEW_SEARCH, props.location.search);
    }, [props.location.search]);

    const isFilteringOnCreatorUserId = Boolean(
        // @ts-expect-error
        props.activeFilters.find((filter) => {
            return Object.keys(filter)[0] === 'creatorUserId';
        })
    );

    const isShowingArchived = safelyGetFilterByKey(props.activeFilters, 'archived') === 'true';

    const isFiltering = checkIsFiltering(props.activeFilters, props.searchValue);

    const handleCreateNewDripSequence = () => {
        setIsSavingNewDripSequence(true);

        // @ts-expect-error
        props.onCreateDripSequence('Untitled').then((dripSequence) => {
            setIsSavingNewDripSequence(false);
            if (dripSequence) {
                appHistory.push(dripSequence.htmlUrlPath);
            }
        });
    };

    // Auto-create drip sequence when triggered from command palette
    React.useEffect(() => {
        if (autoCreateRef.current) {
            autoCreateRef.current = false;
            handleCreateNewDripSequence();
        }
    });

    const duplicatingDripSequence = props.dripSequences.find(
        // @ts-expect-error
        (sequence) => sequence.id === duplicatingDripSequenceId
    );

    const tabOptions = [
        {
            label: 'Board',
            id: 'board',
            icon: BoardIcon,
            href: props.getUpdatedUrl({
                view: 'board',
            }),
            component: (
                <DripSequencesBoardView
                    isLoading={props.isLoading}
                    isFiltering={isFiltering}
                    isShowingArchived={isShowingArchived}
                    error={props.error}
                    dripSequences={props.dripSequences}
                    onDeleteDripSequence={props.onDeleteDripSequence}
                    onDuplicateDripSequence={(id) => setDuplicatingDripSequenceId(id)}
                    onRestoreDripSequence={props.onRestoreDripSequence}
                    handleCreateNewDripSequence={handleCreateNewDripSequence}
                />
            ),
        },
        {
            label: 'Table',
            id: 'table',
            icon: ListIcon,
            href: props.getUpdatedUrl({
                view: 'table',
            }),
            component: (
                <DripSequencesListView
                    isLoading={props.isLoading}
                    error={props.error}
                    dripSequences={props.dripSequences}
                    sortString={props.urlParams.sort}
                    isFiltering={isFiltering}
                    isShowingArchived={isShowingArchived}
                    onChangeSort={(newSortString) => {
                        props.onParamChange({sort: newSortString}, true);
                    }}
                    handleCreateNewDripSequence={handleCreateNewDripSequence}
                />
            ),
        },
    ];

    return (
        <>
            <PageContainer
                title='Drip sequences'
                pageTitleBar={
                    <PageTitleBar
                        title='Drip sequences'
                        subheaderPrimaryText='Automatically send messages at just the right moment'
                        buttons={
                            <Button
                                variant='primary'
                                size='big'
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleCreateNewDripSequence();
                                }}
                            >
                                {isSavingNewDripSequence
                                    ? 'Creating new drip sequence…'
                                    : 'Create new drip sequence'}
                            </Button>
                        }
                    />
                }
                controlBar={
                    <ListPageControlBar
                        archiveTab={{
                            isActive: isShowingArchived,
                            onChange: () => {
                                props.onUpdateFilters({
                                    archived: isShowingArchived ? null : true,
                                });
                            },
                        }}
                        tabs={{
                            selectedTab: selectedTab,
                            options: tabOptions,
                        }}
                        leftSection={
                            <ExpandedFilterBar
                                statusFilterProps={{
                                    statusFilter: safelyGetFilterByKey(
                                        props.activeFilters,
                                        'status'
                                    ),
                                    statusOptions: [
                                        'Draft',
                                        'Active',
                                        'Paused',
                                        'Retired',
                                        'Canceled',
                                        'Problem',
                                    ],
                                    statusDisabled: isShowingArchived,
                                }}
                                // @ts-expect-error
                                lastUpdateFilterProps={{
                                    lastUpdatedFilter: safelyGetFilterByKey(
                                        props.activeFilters,
                                        'lastUpdated'
                                    ),
                                    lastUpdatedOptions: LAST_UPDATED_VALUE_OPTIONS,
                                }}
                                justMineFilterProps={{
                                    isFilteringOnCreatorUserId: isFilteringOnCreatorUserId,
                                    userId: props.userId,
                                }}
                                searchFilterProps={{
                                    searchValue: props.searchValue,
                                    onUpdateSearch: (newSearchValue) => {
                                        props.onParamChange({q: newSearchValue}, true);
                                    },
                                }}
                                isFiltering={isFiltering}
                                noun='drip sequence'
                                count={props.dripSequences.length}
                                onUpdateFilters={props.onUpdateFilters}
                                onRemoveAllFilters={props.onRemoveAllFilters}
                                activeFilters={props.activeFilters}
                            />
                        }
                    />
                }
            >
                <MarketingStatsBanner isImportUnsubscribesBannerNeeded={true} />
                {/* @ts-expect-error */}
                <TabbedContent selectedTab={selectedTab} tabOptions={tabOptions} />
            </PageContainer>
            {duplicatingDripSequence ? (
                <SingleInputModal
                    requireValue={true}
                    buttonText='Make a copy'
                    title='Name this drip sequence'
                    subtitle='Add a descriptive name so you can find it later'
                    initialValue={`Copy of ${duplicatingDripSequence.name}`}
                    isSaving={isSavingNewDripSequence}
                    onSave={(name) => {
                        setIsSavingNewDripSequence(true);

                        props
                            .onDuplicateDripSequence(name, duplicatingDripSequence.id)
                            // @ts-expect-error
                            .then((dripSequence) => {
                                setDuplicatingDripSequenceId(null);
                                setIsSavingNewDripSequence(false);
                                if (dripSequence) {
                                    appHistory.push(dripSequence.htmlUrlPath);
                                }
                            });
                    }}
                    onClose={() => {
                        setDuplicatingDripSequenceId(null);
                    }}
                />
            ) : null}
        </>
    );
}

export function DripSequencesListPage(props: OwnProps) {
    // We've hardcoded the server's sort for now - table sorting is currently
    // handled on the client and stored in the URL
    const sortValue = 'MODIFIED_TIME_DESC';
    const searchValue = props.urlParams.q || '';
    const isArchived = getArchivedValue(props.activeFilters);

    const queryVariables = getDripSequenceListQueryVariables(isArchived, sortValue, searchValue);

    const isFilteringOnCreatorUserId = Boolean(
        // @ts-expect-error
        props.activeFilters.find((filter) => {
            return Object.keys(filter)[0] === 'creatorUserId';
        })
    );

    const lastUpdatedTime = getLastUpdatedValue(props.activeFilters);
    const statuses = getCampaignStatusesForQuery(props.activeFilters);

    return (
        <GetSessionUser>
            {/* @ts-expect-error */}
            {({user}) => {
                return user ? (
                    <DripSequenceListQuery
                        isArchived={isArchived}
                        sortOrder={sortValue}
                        searchQuery={searchValue}
                        lastUpdatedTime={lastUpdatedTime}
                        creatorUserId={isFilteringOnCreatorUserId ? user.id : undefined}
                        statuses={statuses && statuses.length ? statuses : undefined}
                    >
                        {({dripSequences, isLoading, error}) => (
                            <DripSequenceMutations>
                                {({onCreate, onDuplicate, onDelete, onRestore}) => (
                                    <DripSequencesListPageComponent
                                        {...props}
                                        isLoading={isLoading}
                                        error={error}
                                        dripSequences={dripSequences}
                                        // @ts-expect-error
                                        onCreateDripSequence={(name) =>
                                            onCreate(name, queryVariables)
                                        }
                                        // @ts-expect-error
                                        onDuplicateDripSequence={(name, id) =>
                                            onDuplicate(name, id, queryVariables)
                                        }
                                        // @ts-expect-error
                                        onDeleteDripSequence={(dripSequenceId) =>
                                            onDelete(dripSequenceId, queryVariables)
                                        }
                                        // @ts-expect-error
                                        onRestoreDripSequence={(dripSequenceId) =>
                                            onRestore(dripSequenceId, queryVariables)
                                        }
                                        sortValue={sortValue}
                                        searchValue={searchValue}
                                        selectedTab={props.urlParams.view}
                                        userId={user.id}
                                    />
                                )}
                            </DripSequenceMutations>
                        )}
                    </DripSequenceListQuery>
                ) : null;
            }}
        </GetSessionUser>
    );
}
